import type { GetServerSidePropsContext } from 'next'
import { TRACK_PROPS } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { RegularFooter } from 'components/Footer/RegularFooter'
import { CoreNavigation } from 'components/Navigation/consumer'
import { trackPen7565BackendEV } from 'experiments/pen-7565_v1_promo_banner_200_classes/server'
import { trackPen7584BackendEV } from 'experiments/pen-7584_v1_promo_banner_all_classes/server'
import { trackPen7680BackendEV } from 'experiments/pen-7680_v1_promo_banner_gift_cta/server'
import { trackPen7694BackendEV } from 'experiments/pen-7694_v1_promo_sticky_footer_gift_cta_routing/server'
import {
  ssrCategoryDynamicHero,
  resolveDynamicSubcategoryNames,
} from 'libs/dynamic-hero'
import {
  getDefaultProduct,
  getFreeTrialByToken,
  getOutcomesForCourses,
  getV2Categories,
  getSubcategoriesWithPreview,
  getSubcatsCoursesOutcomes,
  getAllCoursesFromGrowthAPI,
  getCoursesByCategorySlugFromGrowthAPI,
} from 'libs/server/api'
import { getPageData } from 'libs/server/page-data'
import { useSprig } from 'libs/sprig'
import { ALL_CLASSES, JUST_ADDED, TRENDING, redirect } from 'libs/utils'
import { getTrendingInstructors } from 'pages/categories/CategoryPage/dataFetching/getTrendingInstructors'
import { AllCategoryPage } from './AllCategoryPage'
import { CategoryPage } from './CategoryPage'
import { type CategoryPageProps } from './pageProps'
import { redirectSlugMap } from './redirectSlugs'

export default function Categories(props: CategoryPageProps) {
  const isAllCatsPage = props.slug === ALL_CLASSES

  useSprig()

  if (isAllCatsPage) {
    return <AllCategoryPage {...props} />
  }

  return (
    <TrackingParamsProvider
      value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-categories' }}
    >
      <CategoryPage {...props} />
    </TrackingParamsProvider>
  )
}

Categories.pageSettings = {
  analyticsLabel: 'Category',
  showPromo: true,
  navigation: CoreNavigation,
  footer: RegularFooter,
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const {
    query: { slug: ctxSlug = [] },
    req: {
      cookies: { referral_token: referralToken },
    },
  } = ctx

  // instead of /categories/all-classes being the same as /categories, redirect
  if (ctxSlug[0] === ALL_CLASSES) {
    return redirect(ctx, '/categories')
  }

  const slug = ctxSlug[0] || ALL_CLASSES

  // TODO: remove these redirects
  if ([JUST_ADDED, 'recently-viewed', TRENDING].includes(slug)) {
    return redirect(ctx, '/categories')
  }

  const categories = await getV2Categories(ctx)
  const category = categories.find((c) => c.slug === slug) || null
  if (!category && slug !== ALL_CLASSES) {
    const redirectSlug = redirectSlugMap.get(slug)
    if (redirectSlug) {
      return redirect(ctx, `/categories/${redirectSlug}`)
    }
    return {
      notFound: true,
    }
  }

  await trackPen7565BackendEV(ctx)
  await trackPen7584BackendEV(ctx)
  await trackPen7680BackendEV(ctx)
  await trackPen7694BackendEV(ctx)

  const {
    savedDynamicHero: categorySavedDynamicHero,
    dynamicSubcategoryNames: categoryDynamicSubcategoryNames,
  } =
    slug !== ALL_CLASSES
      ? await ssrCategoryDynamicHero(ctx, slug)
      : {
          savedDynamicHero: null,
          dynamicSubcategoryNames: null,
        }

  const dynamicSubcategoryNames =
    slug !== ALL_CLASSES
      ? resolveDynamicSubcategoryNames(
          ctx,
          categoryDynamicSubcategoryNames,
          slug,
        )
      : null

  return getPageData({
    criticalPromises: {
      courses:
        slug === ALL_CLASSES
          ? getAllCoursesFromGrowthAPI(ctx)
          : getCoursesByCategorySlugFromGrowthAPI(ctx, slug),
      subcategories:
        slug !== ALL_CLASSES
          ? getSubcategoriesWithPreview(ctx, slug)
          : Promise.resolve([]),
    },
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      trial: getFreeTrialByToken(ctx, { referralToken }),
      subcatsCoursesOutcome: getSubcatsCoursesOutcomes(ctx),
      instructors: getTrendingInstructors(),
      coursesOutcomes: getOutcomesForCourses(),
    },
    propsMapper: (restProps) => ({
      category,
      categories,
      slug,
      categorySavedDynamicHero,
      dynamicSubcategoryNames,
      ...restProps,
    }),
  })
}
